import React, { useState } from "react";
import { Table } from "../../../../../ui/components/Table";
import { Badge } from "../../../../../ui/components/Badge";
import { Button } from "../../../../../ui/components/Button";
import type { DocumentChangeAuditEntity } from "../../../domain/entities/DocumentChangeAuditEntity";
import { formatDateDDMMYYYY } from "../../../../../shared/utils/formatters";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../../../../config/accounting";
import { DocumentChangeModal } from "./DocumentChangeModal";

interface DocumentChangeAuditTableProps {
  audits: DocumentChangeAuditEntity[];
  loading?: boolean;
}

const formatDateTime = (value: string | null): string => {
  if (!value) return "—";
  return formatDateDDMMYYYY(value) || value;
};

export const DocumentChangeAuditTable: React.FC<
  DocumentChangeAuditTableProps
> = ({ audits, loading = false }) => {
  const [selectedAudit, setSelectedAudit] =
    useState<DocumentChangeAuditEntity | null>(null);
  const postedLabel = getPostedStatusLabel(getStoredAccountingBackend());

  return (
    <>
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Document #</Table.HeaderCell>
            <Table.HeaderCell>Party</Table.HeaderCell>
            <Table.HeaderCell>Uploaded</Table.HeaderCell>
            <Table.HeaderCell>{postedLabel}</Table.HeaderCell>
            <Table.HeaderCell>Changes</Table.HeaderCell>
            <Table.HeaderCell>Posted by</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.HeaderRow>
        }
      >
        {loading && audits.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={8} className="py-8 text-center">
              <span className="text-body font-body text-subtext-color">
                Loading report data...
              </span>
            </Table.Cell>
          </Table.Row>
        ) : audits.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={8} className="py-8 text-center">
              <span className="text-body font-body text-subtext-color">
                No document change audits found for the selected filters.
              </span>
            </Table.Cell>
          </Table.Row>
        ) : (
          audits.map((audit) => {
            const isBill = audit.entityType === "bill";
            const partyLabel = isBill ? "Vendor" : "Customer";

            return (
              <Table.Row
                key={audit.id}
                className="cursor-pointer hover:bg-neutral-50"
                onClick={() => setSelectedAudit(audit)}
              >
                <Table.Cell>
                  <Badge variant={isBill ? "warning" : "brand"}>
                    {isBill ? "Bill" : "Invoice"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {audit.documentNumber || `#${audit.entityId}`}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-col items-start gap-0.5">
                    <span className="text-caption font-caption text-subtext-color">
                      {partyLabel}
                    </span>
                    <span className="text-body font-body text-default-font">
                      {audit.partyName || "—"}
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="whitespace-nowrap text-body font-body text-default-font">
                    {formatDateTime(audit.uploadedAt)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="whitespace-nowrap text-body font-body text-default-font">
                    {formatDateTime(audit.postedToZohoDate)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {audit.hasMeaningfulChanges ? (
                    <Badge variant="warning">
                      {audit.changeCount} change
                      {audit.changeCount === 1 ? "" : "s"}
                    </Badge>
                  ) : (
                    <Badge variant="success">No edits</Badge>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <span className="text-caption font-caption text-default-font">
                    {audit.userName || audit.userEmail || "—"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedAudit(audit);
                    }}
                  >
                    View changes
                  </Button>
                </Table.Cell>
              </Table.Row>
            );
          })
        )}
      </Table>

      <DocumentChangeModal
        audit={selectedAudit}
        open={selectedAudit !== null}
        onClose={() => setSelectedAudit(null)}
      />
    </>
  );
};
